import React, { useMemo } from 'react';
import { AlertTriangle, Check, Palette, RotateCcw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    THEME_PRESETS,
    applyTenantTheme,
    clearTenantTheme,
    contrastRatio,
    hasAccessibleContrast,
    parseHex,
} from '@/lib/theme';
import { cn } from '@/lib/utils';

export interface ThemeColorValue {
    theme_primary_color?: string | null;
    theme_accent_color?: string | null;
    theme_radius?: number | null;
    theme_default_mode?: 'light' | 'dark' | 'system' | null;
}

interface ThemeColorPickerProps {
    value: ThemeColorValue;
    onChange: <K extends keyof ThemeColorValue>(field: K, value: ThemeColorValue[K]) => void;
    /** Disables every control, e.g. while the configuration is saving. */
    disabled?: boolean;
}

/** Normalises what a user types: accepts `2563eb`, stores `#2563eb`. */
function normaliseHex(input: string): string {
    const trimmed = input.trim();
    if (!trimmed) return '';
    return trimmed.startsWith('#') ? trimmed.toLowerCase() : `#${trimmed.toLowerCase()}`;
}

const ColorField: React.FC<{
    id: string;
    label: string;
    description: string;
    value: string | null | undefined;
    onChange: (hex: string | null) => void;
    disabled?: boolean;
}> = ({ id, label, description, value, onChange, disabled }) => {
    const current = value ?? '';
    const parsed = parseHex(current);
    const isValid = current === '' || parsed !== null;
    const readable = parsed !== null && hasAccessibleContrast(current);

    return (
        <div className="space-y-2">
            <Label htmlFor={id}>{label}</Label>
            <div className="flex items-center gap-2">
                {/* The native swatch is the quickest way to pick; the text field
                    is there so an exact brand hex can be pasted. */}
                <input
                    type="color"
                    aria-label={`${label} swatch`}
                    value={parsed ? current : '#2563eb'}
                    onChange={e => onChange(normaliseHex(e.target.value))}
                    disabled={disabled}
                    className="h-9 w-12 shrink-0 cursor-pointer rounded-md border border-input bg-background p-1 disabled:cursor-not-allowed disabled:opacity-50"
                />
                <Input
                    id={id}
                    value={current}
                    onChange={e => onChange(e.target.value ? normaliseHex(e.target.value) : null)}
                    placeholder="#2563eb"
                    spellCheck={false}
                    disabled={disabled}
                    aria-invalid={!isValid}
                    className={cn('font-mono', !isValid && 'border-destructive')}
                />
                {current && (
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => onChange(null)}
                        disabled={disabled}
                        aria-label={`Clear ${label.toLowerCase()}`}
                        title="Reset to the default palette"
                    >
                        <RotateCcw className="h-4 w-4" />
                    </Button>
                )}
            </div>

            <p className="text-xs text-muted-foreground">{description}</p>

            {!isValid && (
                <p className="text-xs text-destructive">Enter a hex colour, for example #2563eb.</p>
            )}

            {isValid && parsed && !readable && (
                <p className="flex items-start gap-1.5 text-xs text-destructive">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span>
                        Text on this colour is hard to read (best contrast{' '}
                        {Math.max(
                            contrastRatio(current, '#ffffff'),
                            contrastRatio(current, '#111827')
                        ).toFixed(1)}
                        :1, needs 4.5:1). Pick a darker or lighter shade. Saving will be rejected.
                    </span>
                </p>
            )}
        </div>
    );
};

/**
 * Tenant brand colours.
 *
 * Only the brand colours are configurable. Surfaces, text and the status colours
 * stay owned by the design system, so contrast and the light/dark split keep
 * working whatever a tenant picks.
 *
 * Changing a value previews it live on the page; navigating away without saving
 * restores the saved theme, because TenantThemeSetter reapplies from the server
 * configuration whenever it changes.
 */
export const ThemeColorPicker: React.FC<ThemeColorPickerProps> = ({
    value,
    onChange,
    disabled,
}) => {
    const primary = value.theme_primary_color ?? '';
    const accent = value.theme_accent_color ?? '';

    const activePreset = useMemo(
        () =>
            THEME_PRESETS.find(
                p =>
                    p.primary.toLowerCase() === primary.toLowerCase() &&
                    p.accent.toLowerCase() === accent.toLowerCase()
            ),
        [primary, accent]
    );

    /** Applies a change and previews it immediately on the live page. */
    const preview = (next: ThemeColorValue) => {
        const merged = { ...value, ...next };
        if (merged.theme_primary_color || merged.theme_accent_color || merged.theme_radius) {
            applyTenantTheme({
                primary: merged.theme_primary_color,
                accent: merged.theme_accent_color,
                radius: merged.theme_radius,
            });
        } else {
            clearTenantTheme();
        }
    };

    const update = <K extends keyof ThemeColorValue>(field: K, next: ThemeColorValue[K]) => {
        onChange(field, next);
        preview({ [field]: next } as ThemeColorValue);
    };

    return (
        <div className="space-y-6">
            <div className="space-y-3">
                <div>
                    <Label>Preset</Label>
                    <p className="text-xs text-muted-foreground">
                        A starting point. All presets meet the contrast requirement.
                    </p>
                </div>
                <div className="flex flex-wrap gap-2">
                    {THEME_PRESETS.map(preset => {
                        const isActive = activePreset?.id === preset.id;
                        return (
                            <button
                                key={preset.id}
                                type="button"
                                disabled={disabled}
                                onClick={() => {
                                    onChange('theme_primary_color', preset.primary);
                                    onChange('theme_accent_color', preset.accent);
                                    preview({
                                        theme_primary_color: preset.primary,
                                        theme_accent_color: preset.accent,
                                    });
                                }}
                                aria-pressed={isActive}
                                className={cn(
                                    'flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors',
                                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                                    'disabled:cursor-not-allowed disabled:opacity-50',
                                    isActive
                                        ? 'border-primary bg-primary/5'
                                        : 'border-border hover:bg-accent'
                                )}
                            >
                                <span className="flex -space-x-1">
                                    <span
                                        className="h-4 w-4 rounded-full border border-background"
                                        style={{ backgroundColor: preset.primary }}
                                    />
                                    <span
                                        className="h-4 w-4 rounded-full border border-background"
                                        style={{ backgroundColor: preset.accent }}
                                    />
                                </span>
                                {preset.name}
                                {isActive && <Check className="h-3.5 w-3.5 text-primary" />}
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
                <ColorField
                    id="theme_primary_color"
                    label="Primary colour"
                    description="Buttons, links, active navigation and the first chart series."
                    value={value.theme_primary_color}
                    onChange={hex => update('theme_primary_color', hex)}
                    disabled={disabled}
                />
                <ColorField
                    id="theme_accent_color"
                    label="Accent colour"
                    description="Secondary chart series and supporting highlights."
                    value={value.theme_accent_color}
                    onChange={hex => update('theme_accent_color', hex)}
                    disabled={disabled}
                />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                    <Label htmlFor="theme_radius">Corner radius</Label>
                    <div className="flex items-center gap-3">
                        <input
                            id="theme_radius"
                            type="range"
                            min={0}
                            max={24}
                            step={2}
                            value={value.theme_radius ?? 10}
                            onChange={e => update('theme_radius', Number(e.target.value))}
                            disabled={disabled}
                            className="flex-1 accent-[var(--primary)]"
                        />
                        <span className="w-12 text-sm tabular-figures text-muted-foreground">
                            {value.theme_radius ?? 10}px
                        </span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                        Applies to cards, buttons and inputs across the platform.
                    </p>
                </div>

                <div className="space-y-2">
                    <Label htmlFor="theme_default_mode">Default colour mode</Label>
                    <Select
                        value={value.theme_default_mode ?? 'system'}
                        onValueChange={v =>
                            onChange(
                                'theme_default_mode',
                                v as NonNullable<ThemeColorValue['theme_default_mode']>
                            )
                        }
                        disabled={disabled}
                    >
                        <SelectTrigger id="theme_default_mode">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="system">Follow the device</SelectItem>
                            <SelectItem value="light">Light</SelectItem>
                            <SelectItem value="dark">Dark</SelectItem>
                        </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                        What new users see before they choose for themselves.
                    </p>
                </div>
            </div>

            {/* Preview: the surrounding page is already previewing live, but these
                show the brand colour against both text colours side by side. */}
            <div className="space-y-2">
                <Label>Preview</Label>
                <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-card p-4">
                    <Button type="button" disabled={disabled}>
                        <Palette className="h-4 w-4" />
                        Primary action
                    </Button>
                    <Button type="button" variant="outline" disabled={disabled}>
                        Secondary
                    </Button>
                    <span className="inline-flex items-center rounded-full border border-primary/20 bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                        Badge
                    </span>
                    <a
                        href="#theme-preview"
                        onClick={e => e.preventDefault()}
                        className="text-sm text-primary underline-offset-4 hover:underline"
                    >
                        A link
                    </a>
                </div>
            </div>
        </div>
    );
};

export default ThemeColorPicker;
